import { Box } from "@mui/material"
import {
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import clsx from "clsx"
import React, { memo, MouseEvent, useCallback, useEffect } from "react"
import { playerStore } from "src/store/PlayerStore"

import { CuePoint } from "../../../../generated/graphql"
import useTranslate, { Namespaces } from "../../../../hooks/use-translate"
import { s } from "../../../../utilities/common"
import { theme, TI } from "../../../../utilities/theme"
import { DGText } from "../../../common/DGText"
import { PlayerUIFocusKeys } from "../../player-ui-focus-keys"
import { usePlayer } from "../../use-player"
import { useProgress } from "../../use-progress"
import "./ProgressBar.css"
import { videoUIWidth } from "../VideoUI"

const CuepointLabel = () => {
  return (
    <Box
      id={"cuepointLabel"}
      sx={{
        left: 0,
        position: "absolute",
        top: s("-60px"),
        opacity: 0,
        pointerEvents: "none",
      }}
    >
      <Box
        sx={{
          height: s("60px"),
          width: s("300px"),
          maxWidth: s("300px"),
          position: "absolute",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          id={"cuepointLabelBox"}
          sx={{
            height: s("50px"),
            backgroundColor: theme.palette.primary.variant,
            borderRadius: s("8px"),
          }}
        >
          <DGText
            tx={TI.Subheadline08tv}
            sx={{
              whiteSpace: "nowrap",
              pl: s("36px"),
              pt: s("7px"),
              pr: s("36px"),
              overflow: "hidden",
              color: theme.palette.white[70],
              textOverflow: "ellipsis",
            }}
            id={"cuepointLabelText"}
          />
        </Box>
      </Box>
      <Box
        sx={{
          height: s("60px"),
          width: s("300px"),
          maxWidth: s("300px"),
          position: "absolute",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          id={"cuepointLabelBoxReference"}
          sx={{
            opacity: 0,
            height: s("50px"),
            backgroundColor: theme.palette.primary.variant,
            borderRadius: s("8px"),
          }}
        >
          <DGText
            tx={TI.Subheadline08tv}
            sx={{
              whiteSpace: "nowrap",
              pl: s("36px"),
              pt: s("7px"),
              pr: s("36px"),
              overflow: "hidden",
              color: theme.palette.white[70],
              textOverflow: "ellipsis",
            }}
            id={"cuepointLabelTextReference"}
          />
        </Box>
      </Box>
      <Box
        sx={{
          position: "absolute",
          backgroundColor: theme.palette.primary.variant,
          top: s("40px"),
          left: s("144px"),
          width: s("16px"),
          height: s("16px"),
          borderRadius: s("0px 0px 0px 3px"),
          transform: "rotate(45deg)",
        }}
      />
    </Box>
  )
}

export const ProgressBar = () => {
  const { duration } = usePlayer()
  const url = playerStore((state) => state.url)
  const cuePoints = playerStore((state) => state.cuePoints)
  const { startFromProgress } = playerStore((state) => state.navigationProps)
  const t = useTranslate(Namespaces.Common)

  useEffect(() => {
    // make sure the progressbar only resets when the player is expected to play from the beginning
    if (!(typeof startFromProgress === "number"))
      seek({ step: 0, relative: false, instant: true })
  }, [url])

  const { focused, focusSelf, ref } = useFocusable({
    focusKey: PlayerUIFocusKeys.ProgressBar,
    onArrowPress: (direction) => {
      if (direction === "right") {
        seek({ step: 10 })
      }
      if (direction === "left") {
        seek({ step: -10 })
      }
      if (direction === "down") {
        setFocus(PlayerUIFocusKeys.PlayButton)
      }
      return false
    },
  })

  const { seek } = useProgress({ addListeners: true })
  const handlePress = useCallback(
    (e: MouseEvent) => {
      const progressWidth = videoUIWidth
      const mousePosition = e.pageX - s(80) // 80: left page padding
      const clickPosition = Math.min(progressWidth, Math.max(0, mousePosition))
      const pos = Math.round((clickPosition / progressWidth) * duration)
      seek({ step: pos, relative: false, instant: true })
    },
    [duration, seek],
  )

  return (
    <>
      <Box
        tabIndex={0}
        aria-label={t("navigation__progress_steps")}
        ref={ref}
        onMouseEnter={() => focusSelf({ noScroll: true })}
        onMouseDown={handlePress}
        sx={{
          position: "relative",
          height: s("20px"),
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
        className={clsx(focused && "focused")}
      >
        <ProgressOrnaments cuePoints={cuePoints} duration={duration} />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          mt: s("18px"),
        }}
      >
        <DGText
          tx={TI.Subheadline08tv}
          id={"TimestampRefLeft"}
          sx={{
            color: theme.palette.new.surface["100"],
          }}
        >
          00:00
        </DGText>
        <DGText
          tx={TI.Subheadline08tv}
          id={"TimestampRefRight"}
          sx={{
            color: theme.palette.new.surface["100"],
          }}
        >
          00:00
        </DGText>
      </Box>
    </>
  )
}

const ProgressOrnaments = memo(
  ({ duration, cuePoints }: { duration: number; cuePoints: CuePoint[] }) => {
    return (
      <>
        <Box sx={{ position: "relative", height: s("20px") }}>
          <Box
            id={"ProgressRef"}
            sx={{
              transition: "width 0.2s linear",
              height: "100%",
              width: 0,
              left: 0,
              position: "absolute",
              overflowY: "visible",
              zIndex: 3,
            }}
          >
            <Box
              sx={{
                transition: "width 0.2s linear",
                background: "yellow",
                borderBottomLeftRadius: s("12px"),
                borderTopLeftRadius: s("12px"),
                height: s("8px"),
                top: s("6px"),
                width: "100%",
                left: 0,
                position: "absolute",
              }}
            />
            <Box
              id={"hopss"}
              sx={{
                width: "100%",
                height: "100%",
                position: "relative",
                overflowX: "hidden",
                overflowY: "visible",
                top: s("0px"),
              }}
            >
              {duration > 0 &&
                cuePoints.map((cP) => (
                  <Box
                    key={"cuepointYellow-" + cP.mark}
                    sx={{
                      top: s("2px"),
                      backgroundColor: "yellow",
                      height: s("16px"),
                      width: s("16px"),
                      borderRadius: "50%",
                      position: "absolute",
                      left:
                        Math.round((cP.mark / duration) * videoUIWidth) - s(8),
                    }}
                  />
                ))}
            </Box>
            <Box
              sx={{
                transition: "transform 0.2s linear",
                backgroundColor: "white",
                top: 0,
                height: s("20px"),
                width: s("20px"),
                borderRadius: "50%",
                position: "absolute",
                right: s("-10px"),
                bottom: 0,
              }}
            />
          </Box>

          <Box
            className={"white_bar"}
            sx={{
              transition: "width 0.2s linear",
              height: "100%",
              width: "100%",
              left: 0,
              position: "absolute",
              overflowX: "hidden",
              overflowY: "visible",
              zIndex: 2,
            }}
          >
            <Box
              sx={{
                transition: "width 0.2s linear",
                background: "white",
                borderRadius: s("12px"),
                height: s("8px"),
                top: s("6px"),
                width: "100%",
                left: 0,
                position: "absolute",
              }}
            />
            <Box
              sx={{
                width: "100%",
                height: "100%",
                position: "relative",
                top: s("16px"),
              }}
            >
              {duration > 0 &&
                cuePoints.map((cP) => (
                  <Box
                    key={"cuepointWhite-" + cP.mark}
                    sx={{
                      top: s("-14px"),
                      backgroundColor: "white",
                      height: s("16px"),
                      width: s("16px"),
                      borderRadius: "50%",
                      position: "absolute",
                      left:
                        Math.round((cP.mark / duration) * videoUIWidth) - s(8), // 8: half cuepoint width,
                    }}
                  />
                ))}
            </Box>
          </Box>
        </Box>

        <CuepointLabel />
      </>
    )
  },
)
